import { useEffect, useMemo, useState } from "react";
// Redux
import { useDispatch, useSelector } from "react-redux";
import { postArrivalOrDepartureMessage } from "src/redux/actions/flights/FlightData.Actions";
import { IRootState } from "src/redux/reducers/CombinedReducers";
// Formik Imports
import { Form, Formik, FormikHelpers, FormikProps } from "formik";
import {
  FormikDateTimeField,
  FormikSelectField,
  FormikTextField,
} from "src/components/FormikComponents/FormikWrappers";

import { Box, BoxProps, Button, Grid, styled, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ClientSettingName, IClientSettings, IDepartureMessagePayload } from "@vmosolutions/oc-interfaces";
import { CustomFieldsV2 } from "src/components/Custom/CustomFieldsV2";
import { getClientSetting } from "src/redux/actions/App.Actions";
import { formatDelayCode } from "src/utils/delayCode";
import { convertMilli2DaysHrsMin } from "src/utils/index";
import { IDelayOptions } from "../FlightEditor/DelaysForm/DelaysForm";

export interface IDepartureMessages {
  flightId: string;
  windowId: string;
  delayOptions: IDelayOptions[];
  origin: string;
  customFields?: any;
  actualOut: number;
  actualOff: number;
  actualOn: number;
  actualIn: number;
  setRefresh: (refresh: boolean) => void;
}

interface IDepartureMessageValues {
  delays: IDelayOptions[];
  departureMessage: {
    actualOut: number;
    actualOff: number;
    eta: number;
    notes: string;
    custom: object;
  };
}
export type DepartureMessageForm = FormikProps<IDepartureMessageValues>;

const defaultValues: IDepartureMessageValues = {
  delays: [],
  departureMessage: {
    actualOut: undefined,
    actualOff: undefined,
    eta: undefined,
    notes: undefined,
    custom: {},
  },
};

const DepartureMessagesWrapper: React.FC<BoxProps> = styled(Box)(({ theme }) => ({
  minWidth: 363,
  "& .form_footer": {
    marginTop: 10,
    display: "flex",
    justifyContent: "flex-start",
    "& button": { margin: "5px 5px" },
    "& .btn": {
      flexWrap: "wrap",
      display: "flex",
      height: 27,
      fontSize: 10,
      lineHeight: "12px",
      padding: "2px 8px",
      margin: "0px 4px",
      "&.submit": {
        color: theme.custom.forms.inputBack,
      },
    },
  },
  "& .MuiFormLabel-root": {
    minWidth: 150,
  },

  "& p.required_fields_label": {
    margin: 0,
    fontWeight: 300,
    fontSize: 10,
    textTransform: "uppercase",
    color: theme.custom.forms.labels,
    width: "100%",
  },
  "& .required_fields": {
    backgroundColor: alpha(theme.custom.forms.labels, 0.2),
    margin: "2px 0px",
    padding: 10,
  },
}));

// Calculate estimated flight duration based on user inputs.
const flightDuration = (newActualOffDate: number, newActualOnDate: number): string => {
  if (!newActualOffDate || !newActualOnDate) {
    return "";
  }
  return convertMilli2DaysHrsMin(newActualOnDate - newActualOffDate);
};

const validateTimes = (values: IDepartureMessageValues): any => {
  if (!values?.departureMessage) return {};
  const { actualOut, actualOff, eta } = values.departureMessage;
  // nested values have to be added here as empty objects.
  const errors = {
    departureMessage: {} as { [key: string]: string },
  };

  if (!actualOut) {
    errors.departureMessage.actualOut = "Requires actual leave gate date/time.";
  }
  if (!actualOff) {
    errors.departureMessage.actualOff = "Requires actual departure Date/time.";
  }
  if (!eta) {
    errors.departureMessage.actualOn = "Requires actual arrival Date/time.";
  }

  if (!!actualOut && !!actualOff) {
    if (new Date(actualOff) < new Date(actualOut)) {
      errors.departureMessage.actualOff = "Flight cannot depart before it leaves gate.";
      errors.departureMessage.actualOut = "Flight cannot depart before it leaves gate.";
    }
  }

  // Check if actual arrival is after departure.
  if (!!actualOff && !!eta) {
    if (new Date(eta) <= new Date(actualOff)) {
      errors.departureMessage.actualOff = "Flight must depart before its arrival.";
      errors.departureMessage.actualOn = "Flight must arrive after its departure.";
    }
  }

  // Check if actual actualOut is after actualIn.
  if (!!actualOut && !!eta) {
    if (new Date(eta) <= new Date(actualOut)) {
      errors.departureMessage.actualOut = "Flight must leave gate before returning.";
      errors.departureMessage.actualIn = "Flight must return to gate after leaving.";
    }
  }

  return Object.keys(errors?.departureMessage)?.length ? errors : {};
};

/**
 * Movement Message - 'Departure Messages' form.
 * @param props -
 */
export const DepartureMessages = (props: IDepartureMessages): JSX.Element => {
  const {
    // origin,
    flightId,
    delayOptions,
    windowId,
    customFields,
    actualOut,
    actualOff,
    actualOn,
    setRefresh,
  } = props;

  const dispatch = useDispatch();
  const customSchema = useSelector((state: IRootState) => state.customPages?.movementMessages?.departure);
  const delayCodeFormat = useSelector((state: IRootState) => state.UISettings.delayCodeFormat);
  const schema = useMemo(() => (customSchema ? JSON.parse(customSchema) : null), [customSchema]);

  const [delaySetting, setDelaySetting] = useState<IClientSettings | null>();
  const [delayLoaded, setDelayLoaded] = useState<boolean>(false);

  useEffect(() => {
    const getDisplaySetting = async (): Promise<void> => {
      const displaySetting = await getClientSetting(ClientSettingName.DelaySettings);
      setDelaySetting(displaySetting?.setValue?.enabled ? displaySetting : null);
      setDelayLoaded(true);
    };
    getDisplaySetting();
  }, []);

  // Initial values for Departure Message
  const customPath = "departureMessage.custom";
  const initialValues = useMemo(() => {
    const initialCustom: { [key: string]: any } = {};
    if (schema?.properties) {
      Object.keys(schema.properties).forEach((value: string) => {
        // If available, set custom value passed in through object, otherwise use default in schema
        initialCustom[value] =
          customFields?.[value] ??
          schema.properties[value].default ??
          schema.properties[value]?.selectorProps?.defaultValue;
      });
    }
    return {
      ...defaultValues,
      departureMessage: {
        ...defaultValues.departureMessage,
        actualOut,
        actualOff,
        eta: actualOn, // actualOn is mapped to eta for departure messages
        custom: initialCustom,
      },
    };
  }, [actualOut, actualOff, actualOn, customFields, schema]);

  const mosaicPane = useMemo(() => document?.getElementById(windowId + "-panel") ?? null, [windowId]);

  const handleSubmit = async (
    values: IDepartureMessageValues,
    formikHelpers: FormikHelpers<IDepartureMessageValues>
  ): Promise<void> => {
    const { setStatus, setSubmitting, resetForm } = formikHelpers;
    setSubmitting(true);
    setStatus("");

    // Remap delay codes to smaller format.
    const selectedDelayCodes: { codeId: string; onDeparture: boolean }[] = [];
    values?.delays?.forEach((opt: IDelayOptions) => {
      selectedDelayCodes.push({ onDeparture: true, codeId: opt._id });
    });

    const payload: IDepartureMessagePayload = {
      delays: selectedDelayCodes,
      departureMessage: values.departureMessage,
    };

    const response: any = await dispatch(postArrivalOrDepartureMessage(flightId, payload));
    // if successful - post message for user and lock form
    if (response?.success) {
      // setStatus("Departure Message sent Successfully, reset tool to send another.");
      // Set status above no longer needed as setRefresh automatically refreshes tool.
      setRefresh(true);
      // Reset selected delays and notes fields.
      const resetDepMessages: any = { ...values.departureMessage, notes: undefined };
      resetForm({ values: { delays: [], departureMessage: resetDepMessages } });
    } else {
      setStatus("Unable to submit Departure Message, please see administrator for assistance.");
    }
    setSubmitting(false);
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      onSubmit={handleSubmit}
      validate={validateTimes}
    >
      {(departureMessageForm: DepartureMessageForm) => {
        const { status, values, dirty, isValid, isSubmitting, handleReset } = departureMessageForm;
        return (
          <Form>
            <DepartureMessagesWrapper>
              <Grid container className="form_body">
                <p className="required_fields_label">required fields</p>
                <Grid container spacing={0}>
                  <Grid item xs={12}>
                    <FormikDateTimeField
                      className="split_label_input"
                      name="departureMessage.actualOut"
                      label="Out Time (UTC)"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormikDateTimeField
                      className="split_label_input"
                      name="departureMessage.actualOff"
                      label="Off Time (UTC)"
                      //displayOnlyTime={true}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormikDateTimeField
                      className="split_label_input"
                      name="departureMessage.eta"
                      label="Estimated Arrival Time (UTC)"
                      //displayOnlyTime={true}
                    />
                  </Grid>
                  <Typography variant="caption" style={{ fontSize: 12, margin: "5px 15px" }}>
                    {`Estimated Flight Duration: ${flightDuration(
                      values?.departureMessage?.actualOff,
                      values?.departureMessage?.eta
                    )}`}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <FormikSelectField
                    className="delay_codes"
                    name="delays"
                    label="Delay Codes"
                    options={delayOptions ?? []}
                    getOptionLabel={(delay: IDelayOptions) => formatDelayCode(delay, delayCodeFormat)}
                    getOptionValue={(delay: IDelayOptions) => delay.delayCode._id}
                    isMulti={true}
                    isSearchable={true}
                    isClearable={true}
                    menuPosition="fixed"
                    maxMenuHeight={400}
                    menuPortalTarget={mosaicPane}
                    openMenuOnFocus={false}
                    placeholder="Select Delay Codes"
                    filterOptions={{
                      ignoreCase: true,
                      ignoreAccents: true,
                      matchFrom: "any" as const,
                      stringify: (option: any) => `${option.label}`,
                      trim: true,
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormikTextField
                    className="textfield"
                    label={
                      !delayLoaded ? "" : (delaySetting?.setValue?.movementMessageForm?.noteLabel ?? "Departure Note")
                    }
                    name="departureMessage.notes"
                    placeholder=""
                    multiline={true}
                    minRows={3}
                    maxRows={6}
                  />
                </Grid>

                <Grid xs={12}>
                  <CustomFieldsV2 schema={schema} objectPath={customPath} />
                </Grid>

                <Typography variant="caption" color="textPrimary" style={{ margin: "0px 10px" }}>
                  {status}
                </Typography>

                <Grid container className="form_footer">
                  <Button
                    className="btn reset"
                    type="reset"
                    variant="contained"
                    color="secondary"
                    disabled={(!dirty && isValid) || isSubmitting}
                    onClick={handleReset}
                  >
                    Reset
                  </Button>
                  <Button
                    className={"btn save"}
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting || !isValid}
                    type="submit"
                  >
                    Submit
                  </Button>
                </Grid>
              </Grid>
            </DepartureMessagesWrapper>
          </Form>
        );
      }}
    </Formik>
  );
};
